// The conversation-HISTORY rail (BUILD-DEC-098; prototype .chat-hist — flip in compact, rail in full).
// Renders EXACTLY the server's session-list projection — the caller's OWN conversations (OQ-N5 Option A)
// PLUS the Schedule-owned conversations every authenticated human may inspect (owner ruling 2026-08-02).
// The scope lives in the owning module, never UI hiding: this rail adds no row and hides none, it only
// LABELS which class each row belongs to so a routine cannot pass for the reader's own chat. The rows are
// the prototype's own vocabulary (.ch-new / .ch-row / .ch-t / .ch-meta / .ch-out — the CSS shipped with
// the foundation), plus .ch-tag for the routine marker this change needed.
// "New conversation" mints NOTHING client-side: it clears the dock and the NEXT send mints a fresh
// platform-owned Session (the client never self-mints ids). Honest states throughout (P-SELF): a load
// failure says so; zero conversations says so; no fabricated rows.

import { useMemo } from 'react';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import { useProjection } from '../surfaces/useProjection.ts';
import type { ChatController } from '../surfaces/useChat.ts';
import type { ChatRunState, ChatSessionOrigin, ChatSessionRow } from '../surfaces/projections.ts';

/** The honest one-line outcome a history row shows (state + size — derived, never editorialised). A
 *  routine counts RUNS, not exchanges: nobody had an exchange with it, it ran on its own schedule. */
function outcomeLine(state: ChatRunState, runCount: number, origin: ChatSessionOrigin): string {
  const noun = origin === 'schedule' ? 'run' : 'exchange';
  const size = runCount === 1 ? `1 ${noun}` : `${String(runCount)} ${noun}s`;
  switch (state) {
    case 'running':
      return `In progress · ${size}`;
    case 'completed':
      return `Completed · ${size}`;
    case 'failed':
      return `Failed · ${size}`;
    case 'interrupted':
      return `Stopped · ${size}`;
  }
}

/** The row's headline. A private chat shows what was asked; a routine shows its Schedule name. Neither is
 *  ever invented: an unresolved name falls back to the honest class label, not to a plausible title. */
function titleLine(row: ChatSessionRow): string {
  if (row.title.length > 0) return row.title;
  return row.origin === 'schedule' ? 'Scheduled routine' : 'Conversation';
}

/** A compact local-time stamp for the row's `when` line (the server supplies the ISO instant). */
function whenLine(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** One conversation row — opens that session's full transcript in the dock (mints nothing). A Schedule's
 *  conversation carries a visible ROUTINE tag so it reads as the platform's own work rather than
 *  masquerading as something the reader said (owner ruling 2026-08-02: see what happened and who said what). */
function HistoryRow({
  row,
  active,
  onOpen,
}: {
  readonly row: ChatSessionRow;
  readonly active: boolean;
  readonly onOpen: () => void;
}): React.JSX.Element {
  return (
    <button type="button" className={active ? 'ch-row on' : 'ch-row'} onClick={onOpen}>
      {row.origin === 'schedule' ? <span className="ch-tag">Routine</span> : null}
      <span className="ch-t">{titleLine(row)}</span>
      <span className="ch-meta">{whenLine(row.lastActivityAt)}</span>
      <span className="ch-out">{outcomeLine(row.state, row.runCount, row.origin)}</span>
    </button>
  );
}

interface ChatHistoryProps {
  readonly chat: ChatController;
  /** Dock-owned switch callbacks enforce controller invalidation before selecting the matching draft. */
  readonly onOpenConversation: (sessionId: string) => void;
  readonly onNewConversation: () => void;
}

/** The history rail: New conversation + the owner-scoped list, newest activity first. */
export function ChatHistory({
  chat,
  onOpenConversation,
  onNewConversation,
}: ChatHistoryProps): React.JSX.Element {
  const api = useSurfaceApi();
  // Memoised per mount: the rail re-mounts on every history toggle, so each open re-reads the list —
  // the dashboard caches nothing durable (Constraint 4).
  const load = useMemo(() => () => api.getChatSessions(), [api]);
  const sessions = useProjection(load);

  return (
    <aside aria-label="Conversation history" className="chat-hist">
      <button type="button" className="ch-new" onClick={onNewConversation}>
        New conversation
      </button>
      <div className="ch-list">
        {sessions.loading ? <div className="empty-line">Loading conversations…</div> : null}
        {sessions.error ? (
          <div className="empty-line" role="status">
            Conversations are temporarily unavailable.
          </div>
        ) : null}
        {sessions.data !== null && sessions.data.length === 0 ? (
          <div className="empty-line">
            No conversations yet — every conversation stays on the record and will list here.
          </div>
        ) : null}
        {(sessions.data ?? []).map((row) => (
          <HistoryRow
            key={row.sessionId}
            row={row}
            active={row.sessionId === chat.activeSessionId}
            onOpen={() => onOpenConversation(row.sessionId)}
          />
        ))}
      </div>
    </aside>
  );
}
